import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
// Stripe Elements imports removed - admin bookings use invoice system for payments
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Plus, Trash2, Users, Clock, AlertCircle, CheckCircle, CreditCard, Loader2, Lock, X, DollarSign, Calendar, MapPin, Copy, ExternalLink, Navigation, Percent, Building2 } from 'lucide-react';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import DistanceCalculator from './DistanceCalculator';
import RecurringClientSelector from './RecurringClientSelector';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Note: Admin bookings do not collect payment directly - they use the invoice system
// The StripeHostedFields payment form has been removed as it was using incorrect imports
// Payment for admin-created bookings can be collected via invoices or the client portal

const CreateBookingDialog = ({ open, onClose, onSuccess }) => {
  const [classTypes, setClassTypes] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [students, setStudents] = useState([{ id: Date.now(), firstName: '', lastName: '', email: '', phone: '' }]);
  const [loading, setLoading] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  
  // Address state
  const [classAddress, setClassAddress] = useState({
    street: '',
    city: '',
    state: 'NV',
    zip: ''
  });
  const [isOutsideServiceArea, setIsOutsideServiceArea] = useState(false);
  const [isAddressValidated, setIsAddressValidated] = useState(false);
  const [formattedAddress, setFormattedAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  // Public class state
  const [isPublicClass, setIsPublicClass] = useState(false);
  const [maxStudents, setMaxStudents] = useState(10);
  const [locationName, setLocationName] = useState('');
  const [googlePlaceName, setGooglePlaceName] = useState('');
  
  // Stripe payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pricingPreview, setPricingPreview] = useState(null);
  const [stripeOrderData, setStripeOrderData] = useState(null);
  const [paymentTermsAccepted, setPaymentTermsAccepted] = useState(false);
  
  // Manual fee toggles for admin
  const [manualFees, setManualFees] = useState({
    afterHours: false,
    rushFee: false,
    mileage: 0
  });
  const [feeRates, setFeeRates] = useState({
    afterHoursPerStudent: 0,
    rushPerStudent: 0,
    mileagePerMile: 0
  });
  
  // Discount state - type can be 'dollar' or 'percent'
  const [discount, setDiscount] = useState({ type: 'dollar', value: 0, reason: '' });
  
  // Distance calculator state
  const [showDistanceCalc, setShowDistanceCalc] = useState(false);
  
  // Recurring client state
  const [selectedRecurringClient, setSelectedRecurringClient] = useState(null);
  const [recurringClientNotes, setRecurringClientNotes] = useState('');
  const [recurringClientPricing, setRecurringClientPricing] = useState(null); // Custom price info

  useEffect(() => {
    if (open) {
      fetchClassTypes();
      fetchPaymentConfig();
      fetchFeeRates();
      // Reset form
      setSelectedClassId('');
      setBookingDate('');
      setStartTime('');
      setStudents([{ id: Date.now(), firstName: '', lastName: '', email: '', phone: '' }]);
      setCreatedBooking(null);
      setClassAddress({ street: '', city: '', state: 'NV', zip: '' });
      setIsOutsideServiceArea(false);
      setIsAddressValidated(false);
      setFormattedAddress('');
      setAddressError('');
      setShowPaymentModal(false);
      setPaymentTermsAccepted(false);
      setPricingPreview(null);
      setStripeOrderData(null);
      setCouponCode('');
      setCouponApplied(null);
      setCouponError('');
      setIsPublicClass(false);
      setMaxStudents(10);
      setLocationName('');
      setGooglePlaceName('');
      setManualFees({ afterHours: false, rushFee: false, mileage: 0 });
      setDiscount({ type: 'dollar', value: 0, reason: '' });
      setBookingCode('');
      setSelectedRecurringClient(null);
      setRecurringClientNotes('');
      setRecurringClientPricing(null);
    }
  }, [open]);
  
  // Fetch custom price when recurring client + class type are selected
  useEffect(() => {
    const fetchRecurringClientPrice = async () => {
      if (!selectedRecurringClient?.id || !selectedClassId) {
        setRecurringClientPricing(null);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/admin/recurring-clients/${selectedRecurringClient.id}/class-price/${selectedClassId}`);
        setRecurringClientPricing(response.data);
        
        // Show toast if this is a discounted price
        if (response.data.is_discounted) {
          toast.success(`Custom pricing applied: $${response.data.final_price} (saves $${response.data.discount_amount})`);
        } else if (response.data.custom_price !== null) {
          toast.info(`Custom price set: $${response.data.final_price}`);
        }
      } catch (error) {
        console.error('Error fetching recurring client price:', error);
        setRecurringClientPricing(null);
      }
    };
    
    fetchRecurringClientPrice();
  }, [selectedRecurringClient, selectedClassId]);

  // Booking code state
  const [bookingCode, setBookingCode] = useState('');

  const fetchClassTypes = async () => {
    try {
      const response = await axios.get(`${API}/class-types`);
      setClassTypes(response.data);
    } catch (error) {
      toast.error('Failed to load class types');
    }
  };
  
  const fetchPaymentConfig = async () => {
    try {
      const response = await axios.get(`${API}/payment-config`);
      setPaymentConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch payment config');
    }
  };
  
  const fetchFeeRates = async () => {
    try {
      // Fetch WooCommerce products for fee rates - SKUs are uppercase
      const [rushResponse, afterHoursResponse, mileageResponse] = await Promise.all([
        axios.get(`${API}/woo-product-by-sku?sku=RUSH`).catch(() => ({ data: null })),
        axios.get(`${API}/woo-product-by-sku?sku=AFTERHOURS`).catch(() => ({ data: null })),
        axios.get(`${API}/woo-product-by-sku?sku=MILES`).catch(() => ({ data: null }))
      ]);
      
      // Only use WooCommerce prices - no fallbacks
      setFeeRates({
        rushPerStudent: rushResponse.data?.price ? parseFloat(rushResponse.data.price) : 0,
        afterHoursPerStudent: afterHoursResponse.data?.price ? parseFloat(afterHoursResponse.data.price) : 0,
        mileagePerMile: mileageResponse.data?.price ? parseFloat(mileageResponse.data.price) : 0
      });
      
      // Log for debugging
      console.log('Fee rates from WooCommerce:', {
        rush: rushResponse.data?.price,
        afterHours: afterHoursResponse.data?.price,
        mileage: mileageResponse.data?.price
      });
    } catch (error) {
      console.error('Failed to fetch fee rates from WooCommerce:', error);
    }
  };

  const addStudent = () => {
    setStudents([...students, { id: Date.now(), firstName: '', lastName: '', email: '', phone: '' }]);
  };

  const removeStudent = (id) => {
    if (students.length <= 1) {
      toast.error('At least one student is required');
      return;
    }
    setStudents(students.filter(s => s.id !== id));
  };

  const updateStudent = (id, field, value) => {
    setStudents(students.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  // Reno/Sparks area zip codes for travel fee check
  const renoSparksZipCodes = [
    '89501', '89502', '89503', '89504', '89505', '89506', '89507', '89508', '89509', '89510',
    '89511', '89512', '89513', '89515', '89519', '89520', '89521', '89523', '89533', '89555',
    '89557', '89570', '89595', '89599',
    '89431', '89432', '89434', '89435', '89436', '89441'
  ];

  const isInRenoSparksArea = (city, state, zip) => {
    const normalizedCity = city.toLowerCase().trim();
    const normalizedState = state.toUpperCase().trim();
    if (normalizedState !== 'NV' && normalizedState !== 'NEVADA') return false;
    if (normalizedCity === 'reno' || normalizedCity === 'sparks') return true;
    if (renoSparksZipCodes.includes(zip.trim())) return true;
    return false;
  };

  // Handle Google Places address selection
  const handleAddressSelect = (addressData, formatted, placeName) => {
    if (addressData) {
      setClassAddress({
        street: addressData.street || '',
        city: addressData.city || '',
        state: addressData.state || 'NV',
        zip: addressData.zip || ''
      });
      setFormattedAddress(formatted || '');
      setIsAddressValidated(true);
      setAddressError('');
      setGooglePlaceName(placeName || '');
      setLocationName(placeName || ''); // Auto-fill the location name
      
      // Check if in service area
      if (addressData.city && addressData.state && addressData.zip) {
        const inArea = isInRenoSparksArea(addressData.city, addressData.state, addressData.zip);
        setIsOutsideServiceArea(!inArea);
      }
    } else {
      setClassAddress({ street: '', city: '', state: 'NV', zip: '' });
      setFormattedAddress('');
      setIsAddressValidated(false);
      setIsOutsideServiceArea(false);
      setGooglePlaceName('');
      setLocationName('');
    }
  };

  const validateForm = () => {
    if (!selectedClassId) {
      toast.error('Please select a class type');
      return false;
    }
    if (!bookingDate) {
      toast.error('Please select a date');
      return false;
    }
    if (!startTime) {
      toast.error('Please select a start time');
      return false;
    }
    
    // Only validate student info if students have been partially filled in
    const hasAnyStudentData = students.some(s => 
      s.firstName.trim() || s.lastName.trim() || s.email.trim() || s.phone.trim()
    );
    
    if (hasAnyStudentData) {
      for (let i = 0; i < students.length; i++) {
        const s = students[i];
        if (!s.firstName.trim() || !s.lastName.trim() || !s.email.trim() || !s.phone.trim()) {
          toast.error(`Please fill in all fields for Student ${i + 1}, or clear all student fields to create without students`);
          return false;
        }
      }
    }
    return true;
  };

  // Coupon validation functions
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    if (!selectedClassId || !bookingDate || !startTime) {
      setCouponError('Please select a class, date, and time first');
      return;
    }
    
    setCouponLoading(true);
    setCouponError('');
    
    const startDateTime = new Date(`${bookingDate}T${startTime}:00`);
    
    try {
      const response = await axios.post(`${API}/validate-coupon`, {
        coupon_code: couponCode.trim(),
        class_type_id: selectedClassId,
        student_count: students.length || 1,
        start_time: startDateTime.toISOString(),
        class_address: isOutsideServiceArea ? {
          street: classAddress.street,
          city: classAddress.city,
          state: classAddress.state,
          zip: classAddress.zip,
          is_outside_service_area: true
        } : null,
        customer_email: students[0]?.email || null
      });
      
      if (response.data.valid) {
        setCouponApplied(response.data);
        setCouponError('');
        toast.success(`Coupon applied! Discount: $${response.data.discount_amount.toFixed(2)}`);
      } else {
        setCouponError(response.data.error || 'Invalid coupon code');
        setCouponApplied(null);
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError(error.response?.data?.detail || 'Failed to validate coupon');
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    setCouponError('');
  };

  // Stripe Payment Functions
  const handlePayNow = async () => {
    if (!validateForm()) return;
    
    const selectedClass = classTypes.find(c => c.id === selectedClassId);
    const durationHours = selectedClass?.duration_hours || 4;
    const startDateTime = new Date(`${bookingDate}T${startTime}:00`);
    const endDateTime = new Date(startDateTime.getTime() + durationHours * 60 * 60 * 1000);
    
    // Build order data for Stripe
    const orderData = {
      class_type_id: selectedClassId,
      student_count: students.length,
      start_time: startDateTime.toISOString(),
      end_time: endDateTime.toISOString(),
      students: students.map(s => ({
        first_name: s.firstName.trim(),
        last_name: s.lastName.trim(),
        email: s.email.trim(),
        phone: s.phone.trim()
      })),
      class_address: classAddress.street.trim() ? {
        street: classAddress.street.trim(),
        city: classAddress.city.trim(),
        state: classAddress.state.trim(),
        zip: classAddress.zip.trim(),
        is_outside_service_area: isOutsideServiceArea
      } : null,
      booking_type: "regular",
      // Manual fees from admin
      is_rush: manualFees.rushFee,
      is_after_hours: manualFees.afterHours,
      mileage: manualFees.mileage
    };
    
    setStripeOrderData(orderData);
    
    // Fetch pricing preview
    try {
      const response = await axios.post(`${API}/pricing-preview`, {
        class_type_id: selectedClassId,
        student_count: students.length,
        start_time: startDateTime.toISOString(),
        class_address: orderData.class_address,
        recurring_client_id: selectedRecurringClient?.id || null  // Pass recurring client for custom pricing
      });
      setPricingPreview(response.data);
    } catch (error) {
      console.error('Pricing preview error:', error);
    }
    
    setShowPaymentModal(true);
  };
  
  const createStripeOrder = useCallback(async () => {
    try {
      // Get selected class type info
      const classType = classTypes.find(ct => ct.id === selectedClassId);
      
      // Use recurring client custom price if available, otherwise default class price
      const classPrice = recurringClientPricing?.final_price ?? 
                         pricingPreview?.course?.price_per_student ?? 
                         classType?.price ?? 0;
      
      // Build items array like POS
      const items = students.map((student, idx) => ({
        sku: selectedClassId,
        name: `${classType?.name || 'Class'} - ${student.firstName} ${student.lastName}`,
        quantity: 1,
        price: classPrice
      }));
      
      // Calculate discount amount inline
      const subtotal = (classPrice * students.length) +
        (manualFees.afterHours ? feeRates.afterHoursPerStudent * students.length : 0) +
        (manualFees.rushFee ? feeRates.rushPerStudent * students.length : 0) +
        (manualFees.mileage * feeRates.mileagePerMile);
      
      let discountAmount = 0;
      if (discount.value && discount.value > 0) {
        if (discount.type === 'percent') {
          discountAmount = (subtotal * Math.min(discount.value, 100)) / 100;
        } else {
          discountAmount = Math.min(discount.value, subtotal);
        }
      }
      
      const response = await axios.post(`${API}/pos/create-order`, {
        items: items,
        fees: {
          after_hours: manualFees.afterHours || false,
          rush: manualFees.rushFee || false,
          mileage: manualFees.mileage || 0
        },
        discount: {
          type: discount.type,
          value: discount.value || 0,
          amount: discountAmount,
          reason: discount.reason || ''
        },
        total_quantity: students.length,
        recurring_client_id: selectedRecurringClient?.id || null  // Pass for tracking
      });
      
      return response.data.stripe_order_id;
    } catch (error) {
      console.error('Stripe order creation error:', error);
      toast.error('Failed to create payment. Please try again.');
      throw error;
    }
  }, [selectedClassId, classTypes, students, manualFees, discount, feeRates, recurringClientPricing, pricingPreview, selectedRecurringClient]);
  
  // Memoized Stripe hosted fields styles to prevent re-render issues
  const stripeFieldStyles = useMemo(() => ({
    '.valid': { color: '#22c55e' },
    '.invalid': { color: '#ef4444' },
    'input': { 
      'font-size': '16px',
      'font-family': 'inherit',
      'color': '#ffffff',
      'padding': '12px',
      'background-color': 'transparent'
    },
    ':focus': {
      'color': '#ffffff'
    },
    '::placeholder': {
      'color': '#94a3b8'
    }
  }), []);
  
  // Calculate discount amount based on type
  const getDiscountAmount = () => {
    const subtotal = (pricingPreview?.grand_total || 0) +
      (manualFees.afterHours ? feeRates.afterHoursPerStudent * students.length : 0) +
      (manualFees.rushFee ? feeRates.rushPerStudent * students.length : 0) +
      (manualFees.mileage * feeRates.mileagePerMile);
    
    if (!discount.value || discount.value <= 0) return 0;
    
    if (discount.type === 'percent') {
      const percent = Math.min(discount.value, 100);
      return (subtotal * percent) / 100;
    } else {
      return Math.min(discount.value, subtotal);
    }
  };
  
  const getTotal = () => {
    const subtotal = (pricingPreview?.grand_total || 0) +
      (manualFees.afterHours ? feeRates.afterHoursPerStudent * students.length : 0) +
      (manualFees.rushFee ? feeRates.rushPerStudent * students.length : 0) +
      (manualFees.mileage * feeRates.mileagePerMile);
    return Math.max(0, subtotal - getDiscountAmount());
  };
  
  const onStripeApprove = async (data) => {
    setPaymentProcessing(true);
    try {
      toast.loading('Processing payment...', { id: 'payment' });
      
      const params = new URLSearchParams({
        order_id: data.orderID,
        class_type_id: stripeOrderData.class_type_id,
        student_count: stripeOrderData.student_count.toString(),
        start_time: stripeOrderData.start_time,
        end_time: stripeOrderData.end_time,
        students: JSON.stringify(stripeOrderData.students),
        class_address: JSON.stringify(stripeOrderData.class_address),
        booking_type: "regular"
      });
      
      const response = await axios.post(`${API}/payments/capture-order?${params.toString()}`);
      
      toast.dismiss('payment');
      
      if (response.data.success) {
        toast.success('Payment successful! Booking created.');
        setShowPaymentModal(false);
        setCreatedBooking({
          id: response.data.booking_id,
          class_type_name: classTypes.find(c => c.id === selectedClassId)?.name,
          start_time: stripeOrderData.start_time,
          student_count: stripeOrderData.student_count,
          transaction_id: response.data.transaction_id,
          woo_order_id: response.data.woo_order_id
        });
        onSuccess();
      }
    } catch (error) {
      toast.dismiss('payment');
      toast.error('Payment failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };
  
  const onStripeError = (err) => {
    console.error('Stripe error:', err);
    toast.error('Payment error. Please try again.');
  };
  
  const onStripeCancel = () => {
    toast.info('Payment cancelled.');
    setShowPaymentModal(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const selectedClass = classTypes.find(c => c.id === selectedClassId);
      const durationHours = selectedClass?.duration_hours || 4;
      
      // Create start and end times
      const startDateTime = new Date(`${bookingDate}T${startTime}:00`);
      const endDateTime = new Date(startDateTime.getTime() + durationHours * 60 * 60 * 1000);

      // Filter out empty student entries
      const validStudents = students.filter(s => 
        s.firstName.trim() && s.lastName.trim() && s.email.trim() && s.phone.trim()
      );

      const bookingData = {
        class_type_id: selectedClassId,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        student_count: validStudents.length,
        students: validStudents.map(s => ({
          first_name: s.firstName.trim(),
          last_name: s.lastName.trim(),
          email: s.email.trim(),
          phone: s.phone.trim()
        })),
        class_address: classAddress.street.trim() ? {
          street: classAddress.street.trim(),
          city: classAddress.city.trim(),
          state: classAddress.state.trim(),
          zip: classAddress.zip.trim(),
          is_outside_service_area: isOutsideServiceArea
        } : null,
        location_name: locationName.trim() || googlePlaceName || null,
        is_admin_created: true,  // Flag to skip payment emails for admin-created bookings
        is_public: isPublicClass,  // Whether this is a public class open for enrollment
        max_students: maxStudents,  // Maximum students allowed (default 10)
        coupon_code: couponApplied?.code || null,
        reference_code: bookingCode.trim().toLowerCase() || null,  // Custom booking code (optional)
        // Recurring client data
        recurring_client_id: selectedRecurringClient?.id || null,
        recurring_client_notes: selectedRecurringClient ? recurringClientNotes.trim() : null
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      setCreatedBooking(response.data);
      toast.success('Booking created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const copyPaymentLink = () => {
    if (createdBooking?.woo_cart_url) {
      navigator.clipboard.writeText(createdBooking.woo_cart_url);
      toast.success('Payment link copied to clipboard!');
    }
  };

  // Show success state with payment link
  if (createdBooking) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100" data-testid="booking-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <CheckCircle className="h-5 w-5" />
              Booking Created!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-teal-900/40 border border-teal-700 rounded-lg p-4">
              <p className="font-medium text-teal-300">{createdBooking.class_type_name}</p>
              <p className="text-sm text-teal-200">
                {format(new Date(createdBooking.start_time), 'MMMM d, yyyy')} at {format(new Date(createdBooking.start_time), 'h:mm a')}
              </p>
              <p className="text-sm text-teal-200">{createdBooking.student_count} student(s)</p>
            </div>
            
            {createdBooking.woo_cart_url && (
              <div className="space-y-2">
                <Label className="text-slate-200">Payment Link</Label>
                <div className="flex gap-2">
                  <Input 
                    value={createdBooking.woo_cart_url} 
                    readOnly 
                    className="text-sm bg-slate-700 border-teal-600 text-slate-100"
                  />
                  <Button onClick={copyPaymentLink} variant="outline" size="sm" className="border-teal-600 text-white hover:bg-slate-700">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-white">
                  Share this link with the customer to complete payment
                </p>
              </div>
            )}
            
            {createdBooking.woo_order_id && (
              <p className="text-sm text-white">
                WooCommerce Order: <strong className="text-slate-200">#{createdBooking.woo_order_id}</strong>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={onClose} className="bg-teal-600 hover:bg-teal-500 text-white">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-slate-100" data-testid="create-booking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <Plus className="h-5 w-5 text-teal-400" />
            Create Manual Booking
          </DialogTitle>
          <DialogDescription className="text-white">
            Create a booking manually. A payment link will be generated.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Recurring Client Selector - MOVED TO TOP */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-200">
              <Building2 className="h-4 w-4 text-blue-400" />
              Recurring Client (Optional)
            </Label>
            <div className="bg-slate-700 rounded-lg p-3 border border-slate-600">
              <RecurringClientSelector
                selectedClient={selectedRecurringClient}
                onSelect={(client) => {
                  setSelectedRecurringClient(client);
                  // Check for preferred classes (new format with custom pricing)
                  const preferredClasses = client.preferred_classes || [];
                  // Fallback to old format for backwards compatibility
                  const preferredClassIds = preferredClasses.length > 0 
                    ? preferredClasses.map(pc => pc.class_id)
                    : (client.preferred_class_ids || []);
                  
                  if (preferredClassIds.length > 0) {
                    const preferredClassTypes = classTypes.filter(ct => 
                      preferredClassIds.includes(ct.id)
                    );
                    
                    if (preferredClassTypes.length === 1) {
                      // Auto-select the single preferred class
                      setSelectedClassId(preferredClassTypes[0].id);
                      toast.success(`Auto-selected ${preferredClassTypes[0].name} (client's preferred class)`);
                    } else if (preferredClassTypes.length > 1) {
                      // Show a toast to inform about multiple preferred classes
                      toast.info(`${client.first_name} has ${preferredClassTypes.length} preferred classes. Select one above.`);
                    }
                  }
                }}
                onClear={() => {
                  setSelectedRecurringClient(null);
                  setRecurringClientPricing(null);
                }}
                placeholder="Search to link a recurring client..."
                className="[&_input]:bg-slate-800 [&_input]:text-white [&_input]:border-slate-600"
              />
              {selectedRecurringClient && (
                <div className="mt-3 space-y-2">
                  {/* Show preferred classes with custom pricing if any */}
                  {(() => {
                    const preferredClasses = selectedRecurringClient.preferred_classes || [];
                    const preferredClassIds = preferredClasses.length > 0 
                      ? preferredClasses.map(pc => pc.class_id)
                      : (selectedRecurringClient.preferred_class_ids || []);
                    
                    if (preferredClassIds.length === 0) return null;
                    
                    return (
                      <div className="flex flex-wrap gap-1 p-2 bg-blue-900/30 rounded-lg border border-blue-600">
                        <span className="text-xs text-blue-300 mr-1">Preferred:</span>
                        {preferredClassIds.map(classId => {
                          const ct = classTypes.find(c => c.id === classId);
                          const preferredClass = preferredClasses.find(pc => pc.class_id === classId);
                          const hasCustomPrice = preferredClass?.custom_price !== null && preferredClass?.custom_price !== undefined;
                          
                          return ct ? (
                            <button
                              key={classId}
                              type="button"
                              onClick={() => setSelectedClassId(classId)}
                              className={`text-xs px-2 py-0.5 rounded-full transition-colors flex items-center gap-1 ${
                                selectedClassId === classId 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-blue-900/50 text-blue-300 hover:bg-blue-800'
                              }`}
                            >
                              {ct.name}
                              {hasCustomPrice && (
                                <span className="text-green-300 text-[10px]">${preferredClass.custom_price}</span>
                              )}
                            </button>
                          ) : null;
                        })}
                      </div>
                    );
                  })()}
                  
                  {/* Show custom pricing info if available */}
                  {recurringClientPricing && (
                    <div className={`p-2 rounded-lg border ${
                      recurringClientPricing.is_discounted 
                        ? 'bg-green-900/30 border-green-600' 
                        : 'bg-blue-900/20 border-blue-600/50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className={`w-4 h-4 ${recurringClientPricing.is_discounted ? 'text-green-400' : 'text-blue-400'}`} />
                          <span className="text-sm">
                            {recurringClientPricing.is_discounted ? (
                              <>
                                <span className="text-green-300 font-semibold">Custom Price: ${recurringClientPricing.final_price}</span>
                                <span className="text-green-400 text-xs ml-2">(saves ${recurringClientPricing.discount_amount})</span>
                              </>
                            ) : recurringClientPricing.custom_price !== null ? (
                              <span className="text-blue-300">Custom Price: ${recurringClientPricing.final_price}</span>
                            ) : (
                              <span className="text-slate-400">Standard Price: ${recurringClientPricing.default_price}</span>
                            )}
                          </span>
                        </div>
                        {recurringClientPricing.is_discounted && (
                          <span className="text-xs text-green-400 bg-green-900/50 px-2 py-0.5 rounded">Discounted</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-xs text-blue-300">
                    <AlertCircle className="h-3 w-3" />
                    Client linked as contact (not a student). Add internal note below.
                  </div>
                  <Input
                    placeholder="Internal note about this booking (visible to admins only)"
                    value={recurringClientNotes}
                    onChange={(e) => setRecurringClientNotes(e.target.value)}
                    className="h-8 text-sm bg-slate-800 border-blue-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="recurring-client-notes"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Class Type Selection */}
          <div className="space-y-2">
            <Label className="text-slate-200">Class Type <span className="text-red-400">*</span></Label>
            <Select value={selectedClassId} onValueChange={setSelectedClassId}>
              <SelectTrigger className="bg-slate-700 border-teal-600 text-slate-100" data-testid="admin-class-select">
                <SelectValue placeholder="Select a class..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-teal-600">
                {classTypes.map((ct) => (
                  <SelectItem key={ct.id} value={ct.id} className="text-slate-100 focus:bg-slate-600">
                    {ct.name} ({ct.duration_hours}h)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-200">Date <span className="text-red-400">*</span></Label>
              <Input
                type="date"
                value={bookingDate}
                onChange={(e) => setBookingDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="admin-booking-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">Start Time <span className="text-red-400">*</span></Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="admin-booking-time"
              />
            </div>
          </div>

          {/* Students Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-slate-200">
                <Users className="h-4 w-4" />
                Students <span className="text-red-400">*</span>
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addStudent}
                className="border-teal-600 text-teal-400 hover:bg-teal-900/30"
                data-testid="admin-add-student"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Student
              </Button>
            </div>
            
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {students.map((student, index) => (
                <div key={student.id} className="p-3 bg-slate-700 rounded-lg border border-teal-600">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">Student {index + 1}</span>
                    {students.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStudent(student.id)}
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="First Name *"
                      value={student.firstName}
                      onChange={(e) => updateStudent(student.id, 'firstName', e.target.value)}
                      className="h-8 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    />
                    <Input
                      placeholder="Last Name *"
                      value={student.lastName}
                      onChange={(e) => updateStudent(student.id, 'lastName', e.target.value)}
                      className="h-8 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    />
                    <Input
                      type="email"
                      placeholder="Email *"
                      value={student.email}
                      onChange={(e) => updateStudent(student.id, 'email', e.target.value)}
                      className="h-8 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    />
                    <Input
                      type="tel"
                      placeholder="Phone *"
                      value={student.phone}
                      onChange={(e) => updateStudent(student.id, 'phone', e.target.value)}
                      className="h-8 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Class Address Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-slate-200">
                <MapPin className="h-4 w-4" />
                Class Location
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowDistanceCalc(true)}
                className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/30 h-7 px-2 text-xs"
                data-testid="open-distance-calc-btn"
              >
                <Navigation className="h-3 w-3 mr-1" />
                Distance Calc
              </Button>
            </div>
            <div className="p-3 bg-slate-700 rounded-lg border border-teal-600 space-y-3">
              {/* Recurring Client Saved Locations Selector */}
              {selectedRecurringClient?.locations?.length > 0 && (
                <div className="p-2 bg-blue-900/30 rounded-lg border border-blue-600">
                  <Label className="text-xs font-semibold text-blue-300 mb-2 block flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {selectedRecurringClient.company || `${selectedRecurringClient.first_name}'s`} Saved Locations
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedRecurringClient.locations.map((loc) => {
                      const fullAddress = loc.address ? 
                        `${loc.address.street || ''}, ${loc.address.city || ''}, ${loc.address.state || ''} ${loc.address.zip || ''}`.replace(/^, /, '').replace(/, ,/g, ',') 
                        : '';
                      return (
                        <div key={loc.id} className="group relative">
                          <button
                            type="button"
                            onClick={() => {
                              // Auto-fill the address fields
                              if (loc.address) {
                                setClassAddress({
                                  street: loc.address.street || '',
                                  city: loc.address.city || '',
                                  state: loc.address.state || 'NV',
                                  zip: loc.address.zip || ''
                                });
                                setFormattedAddress(fullAddress);
                                setIsAddressValidated(true);
                                setAddressError('');
                                setLocationName(loc.name || '');
                                toast.success(`Selected location: ${loc.name}`);
                              }
                            }}
                            className="px-3 py-1.5 text-sm bg-blue-900/50 text-blue-200 hover:bg-blue-800 rounded-lg border border-blue-600 transition-colors flex items-center gap-1.5"
                            data-testid={`client-location-${loc.id}`}
                          >
                            <MapPin className="h-3 w-3" />
                            {loc.name || 'Unnamed Location'}
                          </button>
                          {/* Tooltip showing full address on hover */}
                          {fullAddress && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-slate-600">
                              {fullAddress}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-blue-400/70 mt-2">
                    Click a location to auto-fill, or search below for a different address
                  </p>
                </div>
              )}
              
              <div>
                <p className="text-xs text-white mb-2">Start typing and select from Google suggestions</p>
                <AddressAutocomplete
                  onAddressSelect={handleAddressSelect}
                  initialValue={formattedAddress}
                  placeholder="Enter class location address..."
                  className="h-9 bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                  error={addressError}
                  testId="admin-address-autocomplete"
                />
              </div>
              
              {/* Show selected address details */}
              {isAddressValidated && classAddress.street && (
                <div className="space-y-2">
                  {/* Location Name - Prominent, Above Address */}
                  <div className="p-2 bg-amber-900/40 rounded-lg border-2 border-amber-500" data-testid="admin-location-name-section">
                    <Label className="text-xs font-semibold text-amber-300 mb-1 block flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      Location Name
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter location name"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      className="h-8 text-sm bg-slate-800 border-amber-600 text-amber-100 placeholder:text-slate-500 font-medium"
                      data-testid="admin-location-name-input"
                    />
                    <p className="text-xs text-amber-400/70 mt-1">
                      Shown on calendar & emails
                    </p>
                  </div>
                  
                  {/* Address Verified Section */}
                  <div className="p-2 bg-slate-800/50 rounded-lg border border-teal-600" data-testid="admin-address-details">
                    <div className="flex items-center gap-2 text-xs text-teal-400 mb-1">
                      <CheckCircle className="h-3 w-3" />
                      <span className="font-medium">Address Verified</span>
                    </div>
                    <div className="text-xs text-white">
                      {classAddress.street}, {classAddress.city}, {classAddress.state} {classAddress.zip}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Travel Fee Warning */}
              {isOutsideServiceArea && (
                <div className="p-2 bg-amber-900/40 border border-amber-600 rounded-lg">
                  <p className="text-xs text-amber-300 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    <span><strong>Travel Fee Notice:</strong> This address is outside the Reno/Sparks service area.</span>
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Coupon Code Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-slate-200">
              <DollarSign className="h-4 w-4" />
              Coupon Code
            </Label>
            <div className="p-3 bg-slate-700 rounded-lg border border-teal-600">
              {couponApplied ? (
                <div className="flex items-center justify-between bg-teal-900/30 rounded-lg px-3 py-2 border border-teal-700/50">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span className="text-green-300 text-sm font-medium">{couponApplied.code.toUpperCase()}</span>
                    <span className="text-green-400 text-sm">-${couponApplied.discount_amount.toFixed(2)}</span>
                  </div>
                  <Button
                    onClick={removeCoupon}
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-green-400 hover:text-red-400 hover:bg-transparent"
                    data-testid="admin-remove-coupon-btn"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value);
                        setCouponError('');
                      }}
                      className="flex-1 h-9 bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                      data-testid="admin-coupon-input"
                      onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                    />
                    <Button
                      onClick={applyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      size="sm"
                      className="h-9 bg-teal-600 hover:bg-teal-500 text-white px-4"
                      data-testid="admin-apply-coupon-btn"
                    >
                      {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                  {couponError && (
                    <p className="text-red-400 text-xs flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {couponError}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Public Class Option */}
          <div className="p-4 bg-amber-900/20 rounded-lg border border-amber-700/50">
            <div className="flex items-center gap-3">
              <Checkbox
                id="isPublicClass"
                checked={isPublicClass}
                onCheckedChange={setIsPublicClass}
                className="border-amber-600 data-[state=checked]:bg-amber-600"
                data-testid="admin-public-class-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="isPublicClass" className="text-amber-300 font-medium cursor-pointer">
                  Public Class
                </Label>
                <p className="text-xs text-amber-400/70 mt-0.5">
                  Allow customers to enroll themselves in this class
                </p>
              </div>
            </div>
            
            {/* Max Students - Only show when public class is enabled */}
            {isPublicClass && (
              <div className="mt-3 pt-3 border-t border-amber-700/30">
                <div className="flex items-center gap-3">
                  <Label className="text-amber-300/80 text-sm">Max Students:</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={maxStudents}
                    onChange={(e) => setMaxStudents(e.target.value === '' ? '' : e.target.value)}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value);
                      if (isNaN(val) || val < 1) setMaxStudents(1);
                      else if (val > 50) setMaxStudents(50);
                    }}
                    className="w-20 bg-slate-700 border-amber-600/50 text-amber-200 text-center"
                    data-testid="admin-max-students-input"
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Booking Code Option */}
          <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600/50">
            <Label className="text-slate-300 text-sm">Booking Code (Optional)</Label>
            <p className="text-xs text-slate-400 mt-0.5 mb-2">
              Custom code for customers to look up this class. Leave blank to auto-generate.
            </p>
            <Input
              type="text"
              placeholder="e.g., acme2024"
              value={bookingCode}
              onChange={(e) => setBookingCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
              className="bg-slate-700 border-slate-600 text-white"
              data-testid="admin-booking-code-input"
              maxLength={20}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="border-teal-600 text-white hover:bg-slate-700">Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-slate-600 hover:bg-slate-500 text-white"
            data-testid="admin-create-booking-submit"
          >
            {loading ? 'Creating...' : 'Create (No Payment)'}
          </Button>
          <Button 
            onClick={handlePayNow} 
            disabled={loading || !paymentConfig?.client_id}
            className="bg-teal-600 hover:bg-teal-500 text-white flex items-center gap-2"
            data-testid="admin-pay-now-button"
          >
            <CreditCard className="h-4 w-4" />
            Pay Now
          </Button>
        </DialogFooter>
        
        {/* Stripe Payment Modal */}
        {showPaymentModal && (
          <Dialog open={showPaymentModal} onOpenChange={() => {
            setShowPaymentModal(false);
            setPaymentTermsAccepted(false);
          }}>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-white">
                  <CreditCard className="h-5 w-5 text-teal-400" />
                  Process Payment
                </DialogTitle>
              </DialogHeader>
              
              {/* Pricing Summary with Fee Toggles */}
              <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
                <p className="text-white text-sm mb-2">
                  <strong>Class:</strong> {classTypes.find(c => c.id === selectedClassId)?.name}
                </p>
                <p className="text-white text-sm mb-3">
                  <strong>Students:</strong> {students.length}
                </p>
                
                {/* Line Items */}
                <div className="space-y-2 border-t border-teal-600 pt-3">
                  {pricingPreview && (
                    <>
                      <div className="flex justify-between text-white text-sm">
                        <span>Course ({students.length} × ${pricingPreview.course?.price_per_student || 0})</span>
                        <span>${(pricingPreview.course?.subtotal || 0).toFixed(2)}</span>
                      </div>
                      
                      {/* Recurring Client Discount */}
                      {pricingPreview.recurring_client && (
                        <div className="flex justify-between items-center text-sm bg-green-900/30 rounded px-2 py-1 -mx-2">
                          <span className="text-green-300 flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            {pricingPreview.recurring_client.client_company || pricingPreview.recurring_client.client_name} Discount
                          </span>
                          <span className="text-green-400">
                            -${pricingPreview.recurring_client_discount?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Manual Fee Toggles */}
                  <div className="space-y-2 pt-2">
                    <p className="text-xs text-white uppercase tracking-wide">Additional Fees</p>
                    
                    {/* After Hours Fee */}
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setManualFees(f => ({ ...f, afterHours: !f.afterHours }))}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-all ${
                          manualFees.afterHours 
                            ? 'bg-amber-600 text-white' 
                            : 'bg-slate-600 text-white hover:bg-slate-500'
                        }`}
                      >
                        <Clock className="h-3.5 w-3.5" />
                        After Hours
                        {manualFees.afterHours && <CheckCircle className="h-3.5 w-3.5" />}
                      </button>
                      {manualFees.afterHours && (
                        <span className="text-amber-400 text-sm">
                          +${(feeRates.afterHoursPerStudent * students.length).toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    {/* Rush/Last Minute Fee */}
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setManualFees(f => ({ ...f, rushFee: !f.rushFee }))}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-all ${
                          manualFees.rushFee 
                            ? 'bg-red-600 text-white' 
                            : 'bg-slate-600 text-white hover:bg-slate-500'
                        }`}
                      >
                        <AlertCircle className="h-3.5 w-3.5" />
                        Last Minute
                        {manualFees.rushFee && <CheckCircle className="h-3.5 w-3.5" />}
                      </button>
                      {manualFees.rushFee && (
                        <span className="text-red-400 text-sm">
                          +${(feeRates.rushPerStudent * students.length).toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    {/* Mileage Fee */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-teal-400" />
                        <span className="text-sm text-white">Miles:</span>
                        <Input
                          type="number"
                          min="0"
                          value={manualFees.mileage}
                          onChange={(e) => setManualFees(f => ({ ...f, mileage: e.target.value === '' ? '' : e.target.value }))}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value);
                            if (isNaN(val) || val < 0) setManualFees(f => ({ ...f, mileage: 0 }));
                          }}
                          className="w-16 h-8 bg-slate-600 border-slate-500 text-white text-sm"
                          placeholder="0"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowDistanceCalc(true)}
                          className="h-8 px-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/30"
                          data-testid="booking-distance-calc-btn"
                        >
                          <Navigation className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {manualFees.mileage > 0 && (
                        <span className="text-teal-400 text-sm">
                          +${(feeRates.mileagePerMile * manualFees.mileage).toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    {/* Discount */}
                    <div className="border-t border-teal-600 pt-2 mt-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Percent className="h-3.5 w-3.5 text-purple-400" />
                          <span className="text-sm text-purple-400 font-medium">Discount</span>
                        </div>
                        <div className="flex bg-slate-600 rounded-md p-0.5">
                          <button
                            type="button"
                            onClick={() => setDiscount({ ...discount, type: 'dollar' })}
                            className={`px-2 py-0.5 text-xs rounded transition-colors ${
                              discount.type === 'dollar' 
                                ? 'bg-teal-600 text-white' 
                                : 'text-white hover:text-white'
                            }`}
                          >
                            $
                          </button>
                          <button
                            type="button"
                            onClick={() => setDiscount({ ...discount, type: 'percent' })}
                            className={`px-2 py-0.5 text-xs rounded transition-colors ${
                              discount.type === 'percent' 
                                ? 'bg-teal-600 text-white' 
                                : 'text-white hover:text-white'
                            }`}
                          >
                            %
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="relative w-20">
                          {discount.type === 'dollar' && (
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white text-xs">$</span>
                          )}
                          <Input
                            type="number"
                            min="0"
                            max={discount.type === 'percent' ? 100 : undefined}
                            step={discount.type === 'percent' ? 1 : 0.01}
                            value={discount.value ?? ''}
                            onChange={(e) => setDiscount({ ...discount, value: e.target.value === '' ? '' : e.target.value })}
                            onBlur={(e) => {
                              const val = parseFloat(e.target.value);
                              if (isNaN(val) || val < 0) setDiscount(prev => ({ ...prev, value: 0 }));
                              else if (discount.type === 'percent' && val > 100) setDiscount(prev => ({ ...prev, value: 100 }));
                              else setDiscount(prev => ({ ...prev, value: val }));
                            }}
                            placeholder="0"
                            className={`${discount.type === 'dollar' ? 'pl-5' : 'pl-2'} pr-2 h-8 bg-slate-600 border-slate-500 text-white text-sm`}
                          />
                          {discount.type === 'percent' && (
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-xs">%</span>
                          )}
                        </div>
                        <Input
                          type="text"
                          value={discount.reason}
                          onChange={(e) => setDiscount({ ...discount, reason: e.target.value })}
                          placeholder="Reason"
                          className="flex-1 h-8 bg-slate-600 border-slate-500 text-white text-sm placeholder:text-slate-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Discount Display */}
                {getDiscountAmount() > 0 && (
                  <div className="flex justify-between items-center text-sm mt-2">
                    <span className="text-purple-400">
                      Discount {discount.type === 'percent' ? `(${discount.value}%)` : ''}
                      {discount.reason ? ` - ${discount.reason}` : ''}
                    </span>
                    <span className="text-purple-400">-${getDiscountAmount().toFixed(2)}</span>
                  </div>
                )}
                
                {/* Total */}
                <div className="border-t border-teal-600 mt-3 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Total:</span>
                    <span className="text-teal-400 font-bold text-xl">
                      ${getTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Payment Information */}
              <div className="min-h-[150px]">
                {paymentProcessing ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
                    <span className="ml-3 text-white">Processing...</span>
                  </div>
                ) : (
                  <div className="text-center text-white py-8">
                    <AlertCircle className="h-10 w-10 mx-auto mb-3 text-teal-400" />
                    <p className="text-teal-300 mb-2 font-medium">Admin Booking - Invoice System</p>
                    <p className="text-sm text-slate-300 max-w-md mx-auto">
                      Admin-created bookings do not collect payment directly. 
                      Use the invoice system to send payment requests to clients after the booking is created.
                    </p>
                    <Button
                      onClick={() => setShowPaymentModal(false)}
                      className="mt-4 bg-teal-600 hover:bg-teal-500"
                    >
                      Continue with Booking
                    </Button>
                  </div>
                )}
              </div>
              
              <p className="text-xs text-white/70 text-center pb-4 flex items-center justify-center gap-1">
                <Lock className="h-3 w-3" />
                Payment will be collected via invoice
              </p>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
    
    {/* Distance Calculator Dialog */}
    <DistanceCalculator
      open={showDistanceCalc}
      onClose={() => setShowDistanceCalc(false)}
      onImport={(miles) => setManualFees(f => ({ ...f, mileage: miles }))}
    />
    </>
  );
};

export default CreateBookingDialog;
